/**
 * ProjectGuideModal — Proje Yönetim Sistemi kullanım rehberi.
 *
 * Projeler sayfasındaki info ikonundan açılır; proje sisteminin tamamını
 * (kâr/zarar ekseni, nakit ekseni, kartların anlamı, neyin maliyet sayılıp
 * neyin sayılmadığı) sade bir dille, örnek senaryo ve SSS ile anlatır.
 *
 * Amaç: "cariye ödeme neden maliyete girmiyor?", "avans neden kâr değil?"
 * gibi kafa karışıklıklarının kullanıcıya baştan anlatılması.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Paper,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ProjectGuideModal: React.FC<Props> = ({ open, onClose }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);

  const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
      {children}
    </Typography>
  );

  const yes = <CheckCircleIcon fontSize="small" color="success" />;
  const no = <CancelIcon fontSize="small" color="error" />;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <MenuBookIcon color="primary" />
            <Typography variant="h6">
              {tt('Proje Sistemi Nasıl Çalışır?', 'How Does the Project System Work?')}
            </Typography>
          </Box>
          <IconButton edge="end" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* 1 — Nedir? */}
        <Typography variant="body2" sx={{ mb: 1 }}>
          {tt(
            'Proje sistemi, "bu işten ne kazandım, ne harcadım, kasama ne girdi ne çıktı?" sorusunun cevabını iş (proje) bazında verir. Fatura, teklif, sipariş, irsaliye, kasa, banka, çek/senet, cari, personel ve kredi kartı işlemlerini bir projeye bağlarsınız; sistem o projenin gelirini, maliyetini, kârını ve nakit akışını kendisi hesaplar.',
            'The project system answers "what did I earn, what did I spend, and what cash moved in and out?" per job (project). You link invoices, offers, orders, waybills, cashbox, bank, checks/notes, account, personnel and credit card records to a project; the system calculates its revenue, cost, profit and cash flow for you.'
          )}
        </Typography>

        {/* 2 — Nasıl kullanılır */}
        <SectionTitle>{tt('Nasıl kullanılır? — 3 adım', 'How to use it — 3 steps')}</SectionTitle>
        <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 0.5 } }}>
          <Typography component="li" variant="body2">
            {tt('Bu sayfadan "Yeni Proje" ile projenizi açın (ad yeterli; kod, cari ve tarih isteğe bağlı).',
              'Create your project with "New Project" on this page (a name is enough; code, account and dates are optional).')}
          </Typography>
          <Typography component="li" variant="body2">
            {tt('Belge veya para hareketi girerken formdaki "Proje (opsiyonel)" alanından projeyi seçin. Hepsi bu — başka bir şey yapmanız gerekmez.',
              'When entering a document or money transaction, pick the project in the "Project (optional)" field on the form. That is all — nothing else is needed.')}
          </Typography>
          <Typography component="li" variant="body2">
            {tt('Proje satırına tıklayıp detay sayfasından özet kartlarını ve tüm hareket akışını izleyin; Excel\'e aktarabilirsiniz.',
              'Click the project row to open its detail page with summary cards and the full activity feed; you can export to Excel.')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          {[
            tt('Fatura', 'Invoice'), tt('Teklif', 'Offer'), tt('Sipariş', 'Order'), tt('İrsaliye', 'Waybill'),
            tt('Kasa', 'Cashbox'), tt('Banka', 'Bank'), tt('Çek/Senet', 'Check/Note'), tt('Cari', 'Account'),
            tt('Personel', 'Personnel'), tt('Kredi Kartı', 'Credit Card'),
          ].map((label) => (
            <Chip key={label} size="small" variant="outlined" label={label} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {tt('Proje alanı bulunan formlar. Teklif → sipariş → irsaliye dönüşümlerinde proje bağı otomatik taşınır.',
            'Forms with a project field. The project link is carried automatically through offer → order → waybill conversions.')}
        </Typography>

        {/* 3 — Altın kural */}
        <SectionTitle>{tt('Altın kural: Kâr ayrı, Para ayrı', 'The golden rule: Profit and Cash are separate')}</SectionTitle>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {tt(
            'Sistemin en önemli kuralı budur ve iki ayrı defter gibi düşünebilirsiniz. Kâr defteri belgelere bakar: satış faturası kestiğinizde gelir, alış faturası girdiğinizde maliyet yazılır. Para defteri ise sadece kasanıza/bankanıza gerçekten giren-çıkan parayı sayar. İkisi asla birbirine karıştırılmaz.',
            'This is the most important rule — think of it as two separate ledgers. The profit ledger looks at documents: a sales invoice creates revenue, a purchase invoice creates cost. The cash ledger only counts money that actually enters or leaves your cashbox/bank. The two are never mixed.'
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 260px' }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <TrendingUpIcon fontSize="small" color="success" />
              <Typography variant="subtitle2">{tt('Kâr / Zarar defteri', 'Profit / Loss ledger')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {tt('Belgelerden beslenir: Satış faturası = Gelir, Alış faturası = Maliyet. Kâr = Gelir − Maliyet.',
                'Fed by documents: Sales invoice = Revenue, Purchase invoice = Cost. Profit = Revenue − Cost.')}
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 260px' }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <PaymentsIcon fontSize="small" color="info" />
              <Typography variant="subtitle2">{tt('Para (nakit) defteri', 'Cash ledger')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {tt('Gerçek para hareketlerinden beslenir: Tahsilat = Nakit Giriş, Ödeme = Nakit Çıkış. Net Nakit = Giriş − Çıkış.',
                'Fed by real money movements: Collection = Cash In, Payment = Cash Out. Net Cash = In − Out.')}
            </Typography>
          </Paper>
        </Box>
        <Alert severity="info" sx={{ mb: 1 }}>
          {tt(
            'Bu yüzden müşteriden aldığınız avans kârınızı ARTIRMAZ (henüz fatura yok, sadece para girdi) ve cariye yaptığınız ödeme maliyetinizi ARTIRMAZ (borç kapattınız, gideri alış faturası yazar). Bu bir eksik değil; rakamların şişmesini önleyen bilinçli bir kuraldır.',
            'That is why an advance received from a customer does NOT increase your profit (no invoice yet, only cash arrived) and a payment to a supplier does NOT increase your cost (you settled a debt; the expense is recorded by the purchase invoice). This is not a gap — it is a deliberate rule that prevents inflated figures.'
          )}
        </Alert>

        {/* 4 — Kartlar */}
        <SectionTitle>{tt('Özet kartları ne anlatıyor?', 'What do the summary cards mean?')}</SectionTitle>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{tt('Kart', 'Card')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{tt('Anlamı', 'Meaning')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{tt('Gelir (Faturalanan)', 'Revenue (Invoiced)')}</TableCell>
                <TableCell>{tt('Bu projeye kestiğiniz satış faturalarının toplamı. Para tahsil edilmiş olsun olmasın.',
                  'Total of sales invoices issued for this project — whether collected or not.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Maliyet', 'Cost')}</TableCell>
                <TableCell>{tt('Alış faturaları + personel giderleri + belgesiz serbest kasa/banka giderleri + kredi kartı harcamaları.',
                  'Purchase invoices + personnel expenses + free-form cashbox/bank expenses + credit card usages.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Kâr / Zarar', 'Profit / Loss')}</TableCell>
                <TableCell>{tt('Gelir − Maliyet. Tahsilat ve ödemeler bu rakamı DEĞİŞTİRMEZ.',
                  'Revenue − Cost. Collections and payments do NOT change this figure.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Net Nakit', 'Net Cash')}</TableCell>
                <TableCell>{tt('Tahsilat − Ödeme. Projenin cebinize gerçek etkisi (şu ana kadar).',
                  'Collected − Paid. The project\'s real effect on your pocket (so far).')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{tt('Kalan Alacak', 'Outstanding Receivable')}</TableCell>
                <TableCell>{tt('Gelir − Tahsilat: faturaladığınız ama henüz tahsil etmediğiniz tutar. Tahsilat faturayı aşarsa çip "Fazla Tahsilat (Avans)" olarak görünür — fatura kestikçe erir.',
                  'Revenue − Collected: the amount invoiced but not yet collected. If collections exceed invoicing, the chip shows "Excess Collection (Advance)" — it shrinks as you invoice.')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* 5 — Ne sayılır ne sayılmaz */}
        <SectionTitle>{tt('Hangi işlem neyi etkiler?', 'Which transaction affects what?')}</SectionTitle>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{tt('İşlem', 'Transaction')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{tt('Kâr/Zarar', 'Profit')}</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{tt('Nakit', 'Cash')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{tt('Neden?', 'Why?')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{tt('Satış faturası', 'Sales invoice')}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell>{tt('Gelir yazar; para tahsilatla gelir.', 'Creates revenue; cash arrives with collection.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Alış faturası', 'Purchase invoice')}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell>{tt('Maliyet yazar; para ödemeyle çıkar.', 'Creates cost; cash leaves with payment.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Müşteriden tahsilat (avans dahil)', 'Collection from customer (incl. advance)')}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell>{tt('Sadece Nakit Giriş. Gelir faturayla doğar — avans kâr değildir.',
                  'Cash In only. Revenue is created by the invoice — an advance is not profit.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Cariye (tedarikçiye) ödeme', 'Payment to a supplier account')}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell>{tt('Sadece Nakit Çıkış. Maliyeti alış faturası yazar; ikisi de sayılsa gider iki katına çıkardı.',
                  'Cash Out only. The purchase invoice records the cost; counting both would double the expense.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Kasa/banka serbest gideri (belgesiz; ör. yakıt)', 'Free-form cashbox/bank expense (no document; e.g. fuel)')}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell>{tt('Faturası olmayan giderin tek kaydı budur: hem Maliyet hem Nakit Çıkış.',
                  'The only record of an undocumented expense: both Cost and Cash Out.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Personel işlemi (hakediş/ödeme)', 'Personnel transaction (accrual/payment)')}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell>{tt('Maliyet yazar; nakit etkisi bağlı kasa/banka hareketinden gelir.',
                  'Creates cost; the cash effect comes from the linked cashbox/bank record.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Kredi kartı harcaması', 'Credit card usage')}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell>{tt('Maliyet yazar; nakit çıkışı kart borcunu ödediğinizde oluşur.',
                  'Creates cost; cash out happens when you pay the card debt.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Kredi kartı borç ödemesi', 'Credit card debt payment')}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell>{tt('Sadece Nakit Çıkış — maliyet zaten harcamada sayıldı.',
                  'Cash Out only — the cost was already counted on the usage.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Çek / senet', 'Check / promissory note')}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell align="center">{yes}</TableCell>
                <TableCell>{tt('Yalnız tahsil edildiğinde / ödendiğinde nakde işler.',
                  'Hits cash only when collected / paid.')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tt('Teklif / Sipariş / İrsaliye', 'Offer / Order / Waybill')}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell align="center">{no}</TableCell>
                <TableCell>{tt('Bilgi amaçlı akışta görünür; hiçbir toplama girmez.',
                  'Shown in the feed for information; enters no total.')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary">
          {tt('Aynı ayrım akıştaki renkli çiplerde de görünür: Gelir · Maliyet · Nakit Giriş · Nakit Çıkış · Bilgi.',
            'The same distinction appears as colored chips in the feed: Revenue · Cost · Cash In · Cash Out · Info.')}
        </Typography>

        {/* 6 — Örnek senaryo */}
        <SectionTitle>{tt('Örnek: baştan sona küçük bir proje', 'Example: a small project end to end')}</SectionTitle>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="body2">
              {tt('Müşteriden 20.000 ₺ avans aldınız → Net Nakit +20.000, Kâr 0 (henüz fatura yok).',
                'You received a 20,000 advance → Net Cash +20,000, Profit 0 (no invoice yet).')}
            </Typography>
            <Typography component="li" variant="body2">
              {tt('30.000 ₺ malzeme aldınız, alış faturasını projeye bağladınız → Maliyet 30.000.',
                'You bought 30,000 of material and linked the purchase invoice → Cost 30,000.')}
            </Typography>
            <Typography component="li" variant="body2">
              {tt('Tedarikçiye 30.000 ₺ ödediniz → sadece Nakit Çıkış (maliyet zaten faturada sayıldı).',
                'You paid the supplier 30,000 → Cash Out only (the cost was already counted on the invoice).')}
            </Typography>
            <Typography component="li" variant="body2">
              {tt('İş bitti, 100.000 ₺ satış faturası kestiniz → Gelir 100.000, Kâr 70.000.',
                'Job done — you issued a 100,000 sales invoice → Revenue 100,000, Profit 70,000.')}
            </Typography>
            <Typography component="li" variant="body2">
              {tt('Kalan 80.000 ₺ tahsil ettiniz → Net Nakit 20.000 + 80.000 − 30.000 = 70.000. Kâr ile nakit sonunda buluştu.',
                'You collected the remaining 80,000 → Net Cash 20,000 + 80,000 − 30,000 = 70,000. Profit and cash finally meet.')}
            </Typography>
          </Box>
        </Paper>

        {/* 7 — SSS */}
        <SectionTitle>{tt('Sık sorulan sorular', 'Frequently asked questions')}</SectionTitle>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {tt('Cariye ödeme yaptım ama maliyet görünmüyor. Neden?', 'I paid a supplier but no cost appears. Why?')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {tt(
                'Cariye ödeme "borç kapatma"dır; gideri, borcu doğuran belge (alış faturası) yazar. Alış faturasını projeye bağlarsanız maliyet orada bir kez doğar, ödemeleriniz de yalnız nakit akışına yansır. Fatura hiç girmeden sadece ödeme yaparsanız maliyet hiçbir yerde doğmaz — bu durumda ya alış faturası girin ya da belgesiz gideri kasa/banka "para çek" (serbest gider) ile kaydedin.',
                'A payment to a supplier settles a debt; the expense is recorded by the document that created the debt (the purchase invoice). Link the purchase invoice to the project and the cost is counted there once, while payments only affect cash flow. If you only pay without ever entering an invoice, no cost is created anywhere — enter a purchase invoice, or record an undocumented expense via a free-form cashbox/bank withdrawal.'
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {tt('Avans aldım, kâr neden hâlâ 0?', 'I received an advance — why is profit still 0?')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {tt(
                'Çünkü avans kazanılmış para değil, alınmış paradır. İş iptal olsa iade edersiniz. Kâr, satış faturası kesildiğinde doğar. Avans Net Nakit kartında görünür; böylece "para geldi ama henüz hak edilmedi" durumu net izlenir.',
                'Because an advance is money received, not money earned — if the job is cancelled you refund it. Profit is created when the sales invoice is issued. The advance shows in Net Cash, so "cash arrived but not yet earned" stays visible.'
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {tt('"Fazla Tahsilat (Avans)" çipi ne demek?', 'What does the "Excess Collection (Advance)" chip mean?')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {tt(
                'Müşteriden, kestiğiniz faturaların toplamından daha fazla para tahsil ettiniz demektir (Kalan Alacak = Gelir − Tahsilat eksiye düştü). Kötü bir şey değildir; genellikle avans alındığını gösterir. Fatura kestikçe bu tutar azalır ve normal "Kalan Alacak"a döner.',
                'It means you collected more than you invoiced (Outstanding Receivable = Revenue − Collected went negative). It is not a problem; it usually indicates an advance. As you issue invoices it shrinks and turns back into a normal receivable.'
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {tt('Bir kaydı projeye sonradan bağlayabilir / projeden çıkarabilir miyim?', 'Can I link or unlink a record later?')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {tt(
                'Evet. İlgili kaydı düzenleyip "Proje" alanını değiştirmeniz yeterli; boşaltırsanız bağ kalkar. Özetler anında yeniden hesaplanır — proje toplamları ayrı bir yerde tutulmaz, her zaman bağlı kayıtlardan canlı hesaplanır.',
                'Yes. Edit the record and change the "Project" field; clearing it removes the link. Summaries recalculate instantly — project totals are never stored separately, they are always computed live from linked records.'
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" fontWeight="bold">
              {tt('Projeyi tamamlandı / iptal yaptım, ne değişir?', 'I marked the project completed / cancelled — what changes?')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {tt(
                'Rakamlar silinmez, akış durur denemez — sadece proje yeni kayıt formlarındaki öneri listesinden çıkar (mevcut bağlar korunur). Detay sayfası "rakamlar nihai" notu gösterir. Gerekirse projeyi yeniden aktifleştirebilirsiniz.',
                'Nothing is deleted — the project simply stops being suggested on new record forms (existing links are kept). The detail page shows a "figures are final" note. You can reactivate the project anytime.'
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          {tt(
            'Not: POS / hızlı satış fişleri gibi yüksek hacimli operasyonel kayıtlar bilinçli olarak projeye bağlanmaz; proje sistemi ön muhasebe belgeleri ve para hareketleri için tasarlanmıştır.',
            'Note: high-volume operational records such as POS / quick sale receipts are deliberately not linked to projects; the project system is designed for pre-accounting documents and money movements.'
          )}
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={onClose}>{tt('Anladım', 'Got it')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectGuideModal;
